import type { FC, ReactNode } from 'react';
import { useRef } from 'react';
import { Button, MenuDropdown } from '@george-labs.com/design-system';
import type { ItemPropTypes } from '@george-labs.com/design-system/types/components/Dropdown/MenuDropdownPropTypes';

import { t } from '@luna/i18n';
import { useIsOpen } from '@luna/utils-ui';

import { useImageUploadContext } from '../ImageUploadContext';
import { useImageUploadDropdownFocus } from './useImageUploadDropdownFocus';

interface Props {
    disableRemoveImage: boolean;
    dropdownToggle: ReactNode;
    onUploadClick: () => void;
    onDropdownOpen?: () => void;
}

export const ImageUploadDropdown: FC<Props> = ({
    dropdownToggle,
    disableRemoveImage,
    onUploadClick,
    onDropdownOpen,
}) => {
    const [isOpen, open, close] = useIsOpen(false);
    const { openRemovalModal } = useImageUploadContext();
    const popoverRef = useRef<HTMLDivElement | null>(null);
    const toggleRef = useRef<HTMLButtonElement | null>(null);

    useImageUploadDropdownFocus(toggleRef);

    const openDropdown = () => {
        open();
        onDropdownOpen?.();
        popoverRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    };

    const closeDropdown = () => {
        close();
        toggleRef.current?.focus();
    };

    if (disableRemoveImage) {
        return (
            <Button
                icon={Button.ICON.CAMERA}
                onClick={onUploadClick}
                ref={toggleRef}
                aria-label={t('main.imageUpload.upload')}
                data-cy="image-options-trigger-button"
            >
                {dropdownToggle}
            </Button>
        );
    }

    const imageUploadItems: (ItemPropTypes & { 'data-cy': string })[] = [
        {
            label: t('main.imageUpload.upload'),
            'data-cy': 'upload-image-dropdown-button',
            value: 'upload',
            onClick: onUploadClick,
        },
        {
            label: t('main.imageUpload.remove'),
            'data-cy': 'remove-image-dropdown-button',
            value: 'remove',
            disabled: disableRemoveImage,
            onClick: openRemovalModal,
        },
    ];

    return (
        <MenuDropdown
            popoverRef={popoverRef}
            toggleRef={toggleRef}
            isOpen={isOpen}
            alignment={MenuDropdown.ALIGNMENT.CENTER}
            onOpen={openDropdown}
            onClose={closeDropdown}
            items={imageUploadItems}
            toggle={
                <Button
                    icon={Button.ICON.CAMERA}
                    aria-label={t('main.imageUpload.openImageOptions_a11y')}
                    data-cy="image-options-trigger-button"
                >
                    {dropdownToggle}
                </Button>
            }
        />
    );
};
